import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getTranscript,
  initiateOrGetTranscription,
  getTranscriptUrl,
  requestTranscript,
  TranscribeRequest,
  TranscribeResponse,
} from "../api/transcription";
import { downloadFromUrl } from "../helpers/fileDownload";
import { useTranscriptionPolling } from "./useJobs";
import { JobState } from "../types/job/Job";
import { useEffect } from "react";

export function useTranscribe(customerId?: string, interactionId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["transcribe", customerId, interactionId],
    mutationFn: () => initiateOrGetTranscription(customerId!, interactionId!),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["transcriptionStatus", customerId, interactionId],
      });
    },
    retry: false,
  });
}

export function useTranscript(
  customerId?: string,
  interactionId?: string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ["transcription", customerId, interactionId],
    queryFn: () => getTranscript(customerId!, interactionId!),
    enabled: !!customerId && !!interactionId && enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

interface DownloadTranscriptRequest {
  customerId: string;
  interactionId: string;
}

export const useTranscriptDownload = () => {
  return useMutation<void, Error, DownloadTranscriptRequest>({
    mutationKey: ["downloadTranscript"],
    mutationFn: async ({ customerId, interactionId }) => {
      const transcriptUrl = await getTranscriptUrl(customerId, interactionId);
      await downloadFromUrl(transcriptUrl);
    },
  });
};

export const useTranscriptRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<TranscribeResponse, Error, TranscribeRequest>({
    mutationKey: ["requestTranscript"],
    mutationFn: requestTranscript,
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "transcriptionStatus",
          variables.customerId,
          variables.interactionId,
        ],
      });
    },
  });
};

/**
 * Manages transcription job polling and fetches the transcript when complete.
 * The polling query automatically stops refetching when the job finishes but preserves the final state.
 * @param customerId the customer ID
 * @param interactionId the interaction ID
 * @param transcriptionJobId the job ID from the transcription request
 * @returns job state and polling state
 */
export const useTranscriptionWithPolling = (
  customerId: string | undefined,
  interactionId: string | undefined,
  transcriptionJobId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { data: jobState } = useTranscriptionPolling(
    customerId,
    transcriptionJobId
  );

  useEffect(() => {
    if (jobState === JobState.Complete) {
      queryClient.invalidateQueries({
        queryKey: ["transcription", customerId, interactionId],
      });
    }
  }, [jobState, customerId, interactionId, queryClient]);

  return {
    isPolling: jobState === JobState.InProgress,
    jobState,
  };
};
